import { ConfigConstants } from '../../../config/config-constants';
import type { JsonArrayModel } from '../../../models/complex/array.model';
import type { JsonConfirmationEquipmentModel } from '../../../models/complex/confirmation-equipment.model';
import type { JsonConfirmationFollowupModel } from '../../../models/complex/confirmation-followup.model';
import type { JsonConfirmationModel } from '../../../models/complex/confirmation.model';
import type { JsonFda2579FormModel } from '../../../models/complex/fda2579-form.model';
import type { JsonRadioModel } from '../../../models/complex/radio.model';
import type { JsonTextModel } from '../../../models/primitive/text.model';
import { AccordionOpenedState } from '../sections/accordion-opened.state';
import { ConfirmationComplaintsState } from '../sections/confirmation-complaints.state';
import { ConfirmationEquipmentState } from '../sections/confirmation-equipment.state';
import { ConfirmationExpensesState, ExpenseState } from '../sections/confirmation-expenses.state';
import { ConfirmationFollowupState } from '../sections/confirmation-followup.state';
import {
    ConfirmationInstallationPaperworkState,
    PunchListState,
} from '../sections/confirmation-installation-paperwork.state';
import {
    ConfirmationMeasurementToolsState,
    ConfirmationMeasurementToolState,
} from '../sections/confirmation-measurement-tools.state';
import { ConfirmationMeasuringPointsState } from '../sections/confirmation-measuring-points.state';
import { ConfirmationParticipantsState } from '../sections/confirmation-participants.state';
import { ConfirmationReasonMissingProtocolsState } from '../sections/confirmation-reason-missing-protocols.state';
import { ConfirmationServiceReportState, RecipientState } from '../sections/confirmation-service-report.state';
import { ConfirmationSourceReplacementsState } from '../sections/confirmation-source-replacements.state';
import {
    CodeState,
    ConfirmationTimeRecordingState,
    TimeRecordingEntryState,
} from '../sections/confirmation-time-recording.state';
import { ConfirmationUploadToDocboxState } from '../sections/confirmation-upload-to-docbox.state';
import {
    Fda2579CheckboxState,
    Fda2579ComponentState,
    Fda2579FormState,
    Fda2579OtherComponentState,
} from '../sections/fda2579-form.state';
import { omit, pick } from 'lodash';

/**
 * Per-section conversion between the stored business data and the legacy JSON shape.
 *
 * Every list is defaulted on both sides. The state types declare them as present, but this is the load
 * path for a technician's saved work: a payload written by an older version, or a section stored before a
 * list existed, must degrade to "no entries" rather than throw on the way back into the model.
 *
 * Each migrated section has a pair: `…FromJson` narrows the flattened model output down to the business
 * data the state keeps, and `…ToJson` widens it back to the shape that section's own
 * `*ModelFromJson` reads. Going back through the model's restore — rather than applying values onto a
 * built model here — deliberately leaves the intricate parts where they belong: the CANC override of
 * the commercial-info text, `onEquipmentChange()`, `setTimeOptions()`, the downtime-threshold guard and
 * the reason-tree lookup all stay inside the models.
 *
 * The widened objects are cast to the legacy interfaces: they carry exactly the fields the restore
 * reads, and the fields it does not read are absent by design (they are UI state, or dead ends whose
 * restore targets are never constructed). The exception is the accordion flag, which the model reads from
 * one object of its own rather than from the sections: the `…FromJson` pairs neither take nor produce it —
 * {@link withAccordionOpened} and {@link accordionOpenedStatusFromSections} at the end of this file move it
 * between the two shapes — so the converters that widen by spreading `...state` do carry it into the
 * payload, where the section restores ignore it like any other name they do not read.
 */

/**
 * A section without its accordion flag: what a `…FromJson` narrows the legacy payload down to. The flag is
 * not in that payload — the model keeps all thirteen in one object — so it is attached afterwards.
 */
type SectionBusinessData<T extends AccordionOpenedState> = Omit<T, 'accordionOpened'>;

/** The section payloads, as they sit inside the flattened `JSON.stringify(model)` output. */
type FlattenedSections = Pick<
    JsonConfirmationModel,
    | 'equipment'
    | 'uploadToDocBox'
    | 'complaints'
    | 'followup'
    | 'measuringPoints'
    | 'participants'
    | 'sourceReplacements'
    | 'expenses'
    | 'serviceReport'
    | 'measurementTool'
    | 'timeRecording'
    | 'installationPaperwork'
>;

/**
 * A list section as stored: the legacy `JsonArrayModel` erases its element type to `unknown[]`, and the
 * elements are whatever `JSON.stringify` produced from the live models. Each list converter narrows that
 * to the elements' business fields, so the erasure stops here.
 */
type StoredList = { value?: unknown[] } | undefined;

/** One value wrapper as a field model serialises: the restore reads nothing but `value`. */
type StoredValue<T> = { value?: T } | undefined;

/**
 * Rebuilds the single nesting level a field model's restore reaches through. Not a whole `JsonTextModel`
 * or `JsonNumberModel`: the name and options those declare are rebuilt when the model is constructed and
 * are never read back.
 *
 * @param value the stored business value
 * @returns the wrapper shape the restore reads
 */
function valueToJson<T>(value: T): { value: T } {
    return { value };
}

/**
 * Wraps elements back into the array shape a section's restore reads (`data.<list>.value`).
 *
 * @param elements the converted elements, in their stored order
 * @returns the list shape the restore reads
 */
function listToJson<T>(elements: readonly T[]): { value: T[] } {
    return { value: [...elements] };
}

/**
 * The elements of a stored list, or an empty list when the section was stored without one.
 *
 * @param list the stored list
 * @returns its elements
 */
function storedElements(list: StoredList): unknown[] {
    return list?.value ?? [];
}

/** Business data of one radio answer: the code the user picked, or `undefined` when unanswered. */
type RadioAnswer = string | undefined;

/**
 * As much of a legacy radio payload as the restore path touches. Typed loosely on purpose: the legacy
 * interface types the leaf as `unknown`, and an older payload may not have the nesting at all.
 */
type StoredRadioAnswer = { selectedEntry?: { model?: { value?: unknown } } } | undefined;

const EQUIPMENT_FIELDS = [
    'selectedEquipment',
    'statusInArrival',
    'statusAfterFix',
    'reportCodes',
    'reportText',
    'reasonsCode',
    'infoToCCC',
    'commercialInfoCodes',
    'commercialInfoDesc',
    'isCommercialInfoRequired',
    'downTimeStartDateTime',
    'timeOfRecoveryDateTime',
    'assayNumber',
    'lotNumber',
    'phiDataCode',
    'approver',
    'selectedSrsVpnConnection',
    'checkInOutName',
    'checkInOutPhone',
    'downtimeDurationAcknowledged',
    'noDowntimeDurationFlag',
    'isAppointmentBooked',
] as const satisfies ReadonlyArray<keyof ConfirmationEquipmentState>;

const FOLLOWUP_FIELDS = [
    'followupOptions',
    'shortCrmFollowupOptions',
    'appointmentTime',
    'followUpReasons',
    'followUpUMRCodes',
    'reasonsCode',
    'isMaterialDependent',
    'followupTextLong',
    'followupTextShort',
    'additionalInfo',
    'assayNumber',
    'lotNumber',
    'legalCheckHandoverSafetyInformation',
    'legalCheckHandoverUserDocumentation',
    'remoteWork',
    'startImmediately',
    'appBookingOptionValue',
    'appBookingDuration',
    'isAppointmentConfirmed',
    'installationPaperworkShortText',
    'medPhysicsEvaluation',
] as const satisfies ReadonlyArray<keyof ConfirmationFollowupState>;

/** The legal-check answers and flags, which the legacy payload already stored as codes and booleans. */
const MEASUREMENT_TOOL_FIELDS = [
    'noMeasurementTool',
    'legalCheckIEC62353',
    'legalCheckPCResistance',
    'legalCheckDeviceLeakage',
    'legalCheckNEN1010',
    'legalCheckNEN1010Radio',
    'legalCheckNEN1010Resistance',
    'legalCheckRadioProtection',
    'legalCheckRadioProtectionAcceptanceTest',
    'legalCheckRadioProtectionPartialAcceptanceTest',
    'legalCheckRadioProtectionHealthCheck',
    'legalCheckRadioProtectionConsistencyTest',
    'legalCheckVDE701702',
    'legalCheckVDEPCResistance',
    'legalCheckVDEDeviceLeakage',
    'legalCheckVDEInsulationResistance',
    'legalCheckXRay',
    'legalCheck3551DetachablePowerCord',
    'legalCheck3551PermanentlyConnected',
    'legalCheck3551NonDetachablePowerCord',
    'legalCheckVerifiedSecurityDevice',
    'legalCheckResumptionAfterUserValidation',
    'legalCheckDosage',
    'radioActivitySurveyValue',
    'radioActivityBackground',
] as const satisfies ReadonlyArray<keyof ConfirmationMeasurementToolsState>;

/**
 * Extracts one radio answer: the code, dropped down from the option object the legacy payload held.
 *
 * @param answer the legacy radio payload
 * @returns the selected code, or `undefined` when the question is unanswered
 */
function radioAnswerFromJson(answer: StoredRadioAnswer): RadioAnswer {
    const value = answer?.selectedEntry?.model?.value;
    return typeof value === 'string' ? value : undefined;
}

/**
 * Rebuilds the one nesting level a radio's restore reaches through. An unanswered question stays
 * `undefined`, which the restore turns into "nothing selected" as it always did. The result is
 * deliberately not a whole `JsonRadioModel`: the option list, labels and options it also declares are
 * rebuilt by the model's constructor and never read back from storage.
 *
 * @param code the stored answer code
 * @returns the shape `selectByPredicate` reads, or `undefined`
 */
function radioAnswerToJson(code: RadioAnswer): JsonRadioModel | undefined {
    return code === undefined
        ? undefined
        : ({ selectedEntry: { model: { value: code } } } as unknown as JsonRadioModel);
}

/**
 * @param section the flattened equipment payload, absent when the model built no equipment section
 * @returns its business data
 */
export function equipmentFromJson(
    section: JsonConfirmationEquipmentModel | undefined,
): SectionBusinessData<ConfirmationEquipmentState> | undefined {
    return section && pick(section, EQUIPMENT_FIELDS);
}

/**
 * @param state the stored equipment business data
 * @returns the payload `ConfirmationEquipmentModelFromJson` reads
 */
export function equipmentToJson(
    state: ConfirmationEquipmentState | undefined,
): JsonConfirmationEquipmentModel | undefined {
    return state as unknown as JsonConfirmationEquipmentModel | undefined;
}

/**
 * @param section the flattened docbox payload, absent for a short confirmation
 * @returns its business data
 */
export function uploadToDocboxFromJson(
    section: FlattenedSections['uploadToDocBox'] | undefined,
): SectionBusinessData<ConfirmationUploadToDocboxState> | undefined {
    return (
        section && {
            uploadToDocBoxSelection: section.uploadToDocBoxSelection,
            documentTitle: section.documentTitle,
        }
    );
}

/**
 * @param state the stored docbox business data
 * @returns the payload `ConfirmationUploadToDocboxModelFromJson` reads
 */
export function uploadToDocboxToJson(
    state: ConfirmationUploadToDocboxState | undefined,
): FlattenedSections['uploadToDocBox'] | undefined {
    return (
        state && {
            uploadToDocBoxSelection: state.uploadToDocBoxSelection,
            documentTitle: [...state.documentTitle],
        }
    );
}

/**
 * Has no `…ToJson` counterpart: `ConfirmationModelFromJson` never restored the reason, so the app's
 * converter re-selects it on the rebuilt model instead (see `ConfirmationReasonMissingProtocolModel.restoreReason`).
 *
 * @param section the serialized reason, absent while none is documented
 * @returns the documented reason
 */
export function reasonMissingProtocolsFromJson(
    section: JsonConfirmationModel['reasonMissingProtocols'],
): ConfirmationReasonMissingProtocolsState | undefined {
    return (
        section && {
            reasonCode: section.reasonCode,
            otherReasonDescription: section.otherReasonDescription ?? '',
        }
    );
}

/**
 * Narrows the complaints payload to the five answers plus the QCA groups. The legacy payload carried
 * each question's whole option list, labels and validation options; none of it is business data.
 *
 * @param section the flattened complaints payload, absent when the notification has no questionnaire
 * @returns its business data
 */
export function complaintsFromJson(
    section: FlattenedSections['complaints'] | undefined,
): SectionBusinessData<ConfirmationComplaintsState> | undefined {
    if (!section) {
        return undefined;
    }
    return {
        questionPSI1: radioAnswerFromJson(section.questionPSI1),
        questionPSI2: radioAnswerFromJson(section.questionPSI2),
        questionPSI3: radioAnswerFromJson(section.questionPSI3),
        questionPPI4: radioAnswerFromJson(section.questionPPI4),
        questionCYS5: radioAnswerFromJson(section.questionCYS5),
        ticketTitle: section.ticketTitle,
        qcaCreationSet: section.qcaCreationSet,
        qcaFields: section.qcaFields,
        qcaTexts: section.qcaTexts,
    };
}

/**
 * @param state the stored complaints business data
 * @returns the payload `ConfirmationComplaintsModelFromJson` reads
 */
export function complaintsToJson(
    state: ConfirmationComplaintsState | undefined,
): FlattenedSections['complaints'] | undefined {
    if (!state) {
        return undefined;
    }
    return {
        questionPSI1: radioAnswerToJson(state.questionPSI1),
        questionPSI2: radioAnswerToJson(state.questionPSI2),
        questionPSI3: radioAnswerToJson(state.questionPSI3),
        questionPPI4: radioAnswerToJson(state.questionPPI4),
        questionCYS5: radioAnswerToJson(state.questionCYS5),
        ticketTitle: state.ticketTitle,
        qcaCreationSet: state.qcaCreationSet,
        qcaFields: state.qcaFields,
        qcaTexts: state.qcaTexts,
    };
}

/**
 * @param section the flattened follow-up payload, absent when the model built no follow-up section
 * @returns its business data, with the duration reduced to its value
 */
export function followupFromJson(
    section: JsonConfirmationFollowupModel | undefined,
): SectionBusinessData<ConfirmationFollowupState> | undefined {
    return (
        section && {
            ...(pick(section, FOLLOWUP_FIELDS) as unknown as Omit<ConfirmationFollowupState, 'additionalTime'>),
            additionalTime: section.additionalTime?.value,
        }
    );
}

/**
 * @param state the stored follow-up business data
 * @returns the payload `ConfirmationFollowupModelFromJson` reads
 */
export function followupToJson(
    state: ConfirmationFollowupState | undefined,
): JsonConfirmationFollowupModel | undefined {
    return (
        state &&
        ({
            ...state,
            additionalTime: { value: state.additionalTime },
        } as unknown as JsonConfirmationFollowupModel)
    );
}

/** One stored measuring point, as much of it as the restore touches. */
type StoredMeasuringPoint = {
    measuringPointID?: StoredValue<string>;
    newValue?: StoredValue<number>;
    isNotAvailable?: { model?: StoredValue<boolean> };
};

/**
 * Narrows the measuring points to the reading the technician took per point. Everything else the legacy
 * payload carried — the point's equipment, description, unit, old value, mandatory flag — is rebuilt from
 * the notification's equipment on load, and the restore reads none of it.
 *
 * @param section the flattened measuring-points payload
 * @returns its business data
 */
export function measuringPointsFromJson(
    section: FlattenedSections['measuringPoints'] | undefined,
): SectionBusinessData<ConfirmationMeasuringPointsState> | undefined {
    return (
        section && {
            points: (storedElements(section.points) as StoredMeasuringPoint[]).map((point) => ({
                measuringPointID: point.measuringPointID?.value ?? '',
                newValue: point.newValue?.value,
                isNotAvailable: point.isNotAvailable?.model?.value ?? false,
            })),
        }
    );
}

/**
 * @param state the stored measuring-points business data
 * @returns the payload `ConfirmationMeasuringPointsModelFromJson` reads — it looks each point up by
 * `measuringPointID.value` among the freshly built ones, then restores the reading and the flag
 */
export function measuringPointsToJson(
    state: ConfirmationMeasuringPointsState | undefined,
): FlattenedSections['measuringPoints'] | undefined {
    return (
        state &&
        ({
            points: listToJson(
                (state.points ?? []).map((point) => ({
                    measuringPointID: valueToJson(point.measuringPointID),
                    newValue: valueToJson(point.newValue),
                    isNotAvailable: { model: valueToJson(point.isNotAvailable) },
                })),
            ),
        } as unknown as FlattenedSections['measuringPoints'])
    );
}

/** One stored source replacement: already plain data, bar the date's declared type. */
type StoredSourceReplacement = ConfirmationSourceReplacementsState['sourceReplacements'][number];

/**
 * @param section the flattened source-replacements payload
 * @returns its business data
 */
export function sourceReplacementsFromJson(
    section: FlattenedSections['sourceReplacements'] | undefined,
): SectionBusinessData<ConfirmationSourceReplacementsState> | undefined {
    return (
        section && {
            sourceReplacements: storedElements(section.sourceReplacements) as StoredSourceReplacement[],
        }
    );
}

/**
 * @param state the stored source-replacements business data
 * @returns the payload `ConfirmationSourceReplacementsModelFromJson` reads — each element goes straight
 * into `SourceReplacementModelFromJson`, which takes the seven values as they are
 */
export function sourceReplacementsToJson(
    state: ConfirmationSourceReplacementsState | undefined,
): FlattenedSections['sourceReplacements'] | undefined {
    return (
        state &&
        ({
            sourceReplacements: listToJson(state.sourceReplacements ?? []),
        } as unknown as FlattenedSections['sourceReplacements'])
    );
}

/** One stored participant, as much of it as the restore touches. */
type StoredParticipant = {
    pos?: StoredValue<number>;
    title?: StoredValue<string>;
    firstName?: StoredValue<string>;
    lastName?: StoredValue<string>;
    mail?: StoredValue<string>;
    mobileNumber?: StoredValue<string>;
    date?: StoredValue<string>;
    trainingName?: StoredValue<string>;
    productName?: StoredValue<string>;
    shared?: StoredValue<string>;
    trfName?: StoredValue<string>;
    trlName?: StoredValue<string>;
    duration?: StoredValue<string>;
    isSelected?: StoredValue<boolean>;
    creditType?: StoredRadioAnswer;
    isCustomData?: boolean;
};

/**
 * Narrows each participant to the sixteen values `ParticipantModelFromJson` hands to `ParticipantModel`.
 * The legacy payload stored every one of them as a whole field model — fifteen text models plus a radio
 * with its translated option list — none of which is read back.
 *
 * The defaults mirror the restore's own: an absent mobile number reads as empty, an absent selection as
 * selected, an absent credit type as "not available".
 *
 * @param section the flattened participants payload
 * @returns its business data
 */
export function participantsFromJson(
    section: FlattenedSections['participants'] | undefined,
): SectionBusinessData<ConfirmationParticipantsState> | undefined {
    return (
        section && {
            participantList: (storedElements(section.participantList) as StoredParticipant[]).map((participant) => ({
                pos: participant.pos?.value ?? 0,
                title: participant.title?.value ?? '',
                firstName: participant.firstName?.value ?? '',
                lastName: participant.lastName?.value ?? '',
                mail: participant.mail?.value ?? '',
                mobileNumber: participant.mobileNumber?.value ?? '',
                date: participant.date?.value ?? '',
                trainingName: participant.trainingName?.value ?? '',
                productName: participant.productName?.value ?? '',
                shared: participant.shared?.value ?? '',
                trfName: participant.trfName?.value ?? '',
                trlName: participant.trlName?.value ?? '',
                duration: participant.duration?.value ?? '',
                isSelected: participant.isSelected?.value ?? true,
                creditType:
                    radioAnswerFromJson(participant.creditType) ?? ConfigConstants.ParticipantCreditType.NOT_AVAILABLE,
                isCustomData: participant.isCustomData ?? true,
            })),
        }
    );
}

/**
 * @param state the stored participants business data
 * @returns the payload `ConfirmationParticipantsModelFromJson` reads — each element goes through
 * `ParticipantModelFromJson`, which reads one `value` per field and the credit type's selected code
 */
export function participantsToJson(
    state: ConfirmationParticipantsState | undefined,
): FlattenedSections['participants'] | undefined {
    return (
        state &&
        ({
            participantList: listToJson(
                (state.participantList ?? []).map((participant) => ({
                    pos: valueToJson(participant.pos),
                    title: valueToJson(participant.title),
                    firstName: valueToJson(participant.firstName),
                    lastName: valueToJson(participant.lastName),
                    mail: valueToJson(participant.mail),
                    mobileNumber: valueToJson(participant.mobileNumber),
                    date: valueToJson(participant.date),
                    trainingName: valueToJson(participant.trainingName),
                    productName: valueToJson(participant.productName),
                    shared: valueToJson(participant.shared),
                    trfName: valueToJson(participant.trfName),
                    trlName: valueToJson(participant.trlName),
                    duration: valueToJson(participant.duration),
                    isSelected: valueToJson(participant.isSelected),
                    creditType: radioAnswerToJson(participant.creditType),
                    isCustomData: participant.isCustomData,
                })),
            ),
        } as unknown as FlattenedSections['participants'])
    );
}

/** One stored expense: already business data in the legacy payload. */
type StoredExpense = ExpenseState;

/**
 * @param section the flattened expenses payload, absent when the model built no expenses section
 * @returns its business data
 */
export function expensesFromJson(
    section: FlattenedSections['expenses'] | undefined,
): SectionBusinessData<ConfirmationExpensesState> | undefined {
    const distance = section?.distance as StoredValue<string> & { options?: { readonly?: boolean } };
    return (
        section && {
            notificationId: section.notificationId,
            distance: distance?.value,
            distanceReadonly: distance?.options?.readonly ?? false,
            expenses: storedElements(section.expenses) as StoredExpense[],
            additionalTravelExpenses: section.additionalTravelExpenses,
        }
    );
}

/**
 * @param state the stored expenses business data
 * @returns the payload `ConfirmationExpensesModelFromJson` reads — the distance's value and its readonly
 * option go into the constructor, each expense through `ExpenseModelFromJson`
 */
export function expensesToJson(
    state: ConfirmationExpensesState | undefined,
): FlattenedSections['expenses'] | undefined {
    return (
        state &&
        ({
            notificationId: state.notificationId,
            distance: { value: state.distance, options: { readonly: state.distanceReadonly } },
            expenses: listToJson(state.expenses ?? []),
            additionalTravelExpenses: state.additionalTravelExpenses,
        } as unknown as FlattenedSections['expenses'])
    );
}

/** One stored recipient, as much of it as the restore touches. */
type StoredRecipient = {
    faxEmail?: StoredValue<string>;
    commCategory?: StoredValue<string>;
    isSelected?: { model?: StoredValue<boolean> };
    isEditable?: boolean;
    isDeletable?: boolean;
    isHidden?: boolean;
    isFromEmailSet?: boolean;
    isSubmitted?: boolean;
};

/**
 * Reads a text field that may have been stored either as a plain string (current) or as a whole field
 * model (before the section grew a `toJSON`). The model's restore branches on exactly this.
 *
 * @param stored the stored field
 * @returns its value
 */
function storedTextValue(stored: string | StoredValue<string>): string {
    return (typeof stored === 'object' ? stored?.value : stored) ?? '';
}

/**
 * @param section the flattened service-report payload, absent when the model built no section
 * @returns its business data
 */
export function serviceReportFromJson(
    section: FlattenedSections['serviceReport'] | undefined,
): SectionBusinessData<ConfirmationServiceReportState> | undefined {
    return (
        section && {
            existingRecipients: (storedElements(section.existingRecipients) as StoredRecipient[]).map((recipient) => ({
                faxEmail: recipient.faxEmail?.value ?? '',
                commCategory: recipient.commCategory?.value ?? '',
                isSelected: recipient.isSelected?.model?.value ?? false,
                isEditable: recipient.isEditable ?? false,
                isDeletable: recipient.isDeletable ?? false,
                isHidden: recipient.isHidden ?? false,
                isFromEmailSet: recipient.isFromEmailSet ?? false,
                isSubmitted: recipient.isSubmitted ?? false,
            })),
            customerPurchaseOrderNumber: storedTextValue(section.customerPurchaseOrderNumber),
            serviceReportLanguage: storedTextValue(section.serviceReportLanguage),
            reportVisibilityToCustomer: section.reportVisibilityToCustomer,
        }
    );
}

/**
 * @param state the stored service-report business data
 * @returns the payload `ConfirmationServiceReportModelFromJson` reads — plain strings for the two text
 * fields (its newer branch), each recipient through `RecipientModelFromJson`, and the visibility code it
 * matches by value before deriving the SAP long-text flag
 */
export function serviceReportToJson(
    state: ConfirmationServiceReportState | undefined,
): FlattenedSections['serviceReport'] | undefined {
    return (
        state &&
        ({
            existingRecipients: listToJson(
                (state.existingRecipients ?? []).map((recipient: RecipientState) => ({
                    faxEmail: valueToJson(recipient.faxEmail),
                    commCategory: valueToJson(recipient.commCategory),
                    isSelected: { model: valueToJson(recipient.isSelected) },
                    isEditable: recipient.isEditable,
                    isDeletable: recipient.isDeletable,
                    isHidden: recipient.isHidden,
                    isFromEmailSet: recipient.isFromEmailSet,
                    isSubmitted: recipient.isSubmitted,
                })),
            ),
            customerPurchaseOrderNumber: state.customerPurchaseOrderNumber,
            serviceReportLanguage: state.serviceReportLanguage,
            reportVisibilityToCustomer: state.reportVisibilityToCustomer,
        } as unknown as FlattenedSections['serviceReport'])
    );
}

/** One stored measurement tool, as much of it as the restore touches. */
type StoredMeasurementTool = {
    measurementToolKey?: string;
    measurementToolId?: StoredValue<string> & { options?: { readonly?: boolean } };
    isHidden?: boolean;
    selectedEquipment?: string;
    textDescription?: string;
    gid?: string;
    technicalId?: string;
    calibrationUntil?: string;
    calibration?: { toolDescription?: string; calibrationDate?: string };
};

/**
 * Narrows the measurement tools to what the restore reads. The stored option list per tool
 * (`dropdownEntries`) is dropped: the restore builds fresh entries from `MeasurementToolsService` and
 * passes them in, so the stored copy was never read.
 *
 * @param section the flattened measurement-tools payload
 * @returns its business data
 */
export function measurementToolsFromJson(
    section: FlattenedSections['measurementTool'] | undefined,
): SectionBusinessData<ConfirmationMeasurementToolsState> | undefined {
    return (
        section && {
            ...(pick(section, MEASUREMENT_TOOL_FIELDS) as unknown as Omit<
                ConfirmationMeasurementToolsState,
                'measurementTools' | 'radioActivityModel'
            >),
            measurementTools: (storedElements(section.measurementTools) as StoredMeasurementTool[]).map((tool) => ({
                measurementToolKey: tool.measurementToolKey ?? '',
                measurementToolId: tool.measurementToolId?.value ?? '',
                measurementToolIdReadonly: tool.measurementToolId?.options?.readonly ?? false,
                isHidden: tool.isHidden ?? false,
                selectedEquipment: tool.selectedEquipment ?? '',
                textDescription: tool.textDescription ?? '',
                gid: tool.gid ?? '',
                technicalId: tool.technicalId ?? '',
                calibrationUntil: tool.calibrationUntil ?? '',
                calibration:
                    tool.calibration &&
                    ({
                        toolDescription: tool.calibration.toolDescription ?? '',
                        calibrationDate: tool.calibration.calibrationDate ?? '',
                    } satisfies ConfirmationMeasurementToolState['calibration']),
            })),
            // Already business data: six plain fields the survey row's restore reads one for one.
            radioActivityModel: section.radioActivityModel ?? [],
        }
    );
}

/**
 * @param state the stored measurement-tools business data
 * @returns the payload `ConfirmationMeasurementToolsModelFromJson` reads — each tool through
 * `MeasurementToolModelFromJson` (which takes the id's value and, for a calibrated tool, its readonly
 * option), and each survey row through `RadioActiveModelFromJson`
 */
export function measurementToolsToJson(
    state: ConfirmationMeasurementToolsState | undefined,
): FlattenedSections['measurementTool'] | undefined {
    return (
        state &&
        ({
            ...state,
            measurementTools: listToJson(
                (state.measurementTools ?? []).map((tool: ConfirmationMeasurementToolState) => ({
                    measurementToolKey: tool.measurementToolKey,
                    measurementToolId: {
                        value: tool.measurementToolId,
                        options: { readonly: tool.measurementToolIdReadonly },
                    },
                    isHidden: tool.isHidden,
                    selectedEquipment: tool.selectedEquipment,
                    textDescription: tool.textDescription,
                    gid: tool.gid,
                    technicalId: tool.technicalId,
                    calibrationUntil: tool.calibrationUntil,
                    calibration: tool.calibration,
                })),
            ),
            radioActivityModel: [...(state.radioActivityModel ?? [])],
        } as unknown as FlattenedSections['measurementTool'])
    );
}

/** The class name the legacy payload discriminated the two entry variants on. */
const GENERAL_ENTRY_NAME = 'TimeRecordingGeneralEntryModel';
const LIMITED_ENTRY_NAME = 'TimeRecordingLimitedEntryModel';

/** One stored time-recording entry, loose enough for either variant. */
type StoredTimeRecordingEntry = {
    name?: string;
    startDateTime?: string;
    endDateTime?: string;
    typeOfWork: TimeRecordingEntryState['typeOfWork'];
    billableCode?: string;
    isDurationUnchanged?: boolean;
    activityCode?: string;
    activityCodes?: StoredCode[];
    meansOfTransport?: string;
    destination?: string;
};

/** One stored SAP code: six field models, of which the restore reads the values. */
type StoredCode = {
    pos?: StoredValue<number>;
    codeType?: StoredValue<string>;
    qkatart?: StoredValue<string>;
    codeGroup?: StoredValue<string>;
    codeName?: StoredValue<string>;
    codeDesc?: StoredValue<string>;
};

/**
 * @param code the stored code
 * @returns its six values
 */
function codeFromJson(code: StoredCode): CodeState {
    return {
        pos: code.pos?.value ?? 0,
        codeType: code.codeType?.value ?? '',
        qkatart: code.qkatart?.value ?? '',
        codeGroup: code.codeGroup?.value ?? '',
        codeName: code.codeName?.value ?? '',
        codeDesc: code.codeDesc?.value ?? '',
    };
}

/**
 * @param code the stored code's business data
 * @returns the shape `CodeModelFromJson` reads
 */
function codeToJson(code: CodeState): unknown {
    return {
        pos: valueToJson(code.pos),
        codeType: valueToJson(code.codeType),
        qkatart: valueToJson(code.qkatart),
        codeGroup: valueToJson(code.codeGroup),
        codeName: valueToJson(code.codeName),
        codeDesc: valueToJson(code.codeDesc),
    };
}

/**
 * Narrows one entry, choosing its variant the way the section's restore does — on the stored model class
 * name — and replacing that with this state's own `kind`.
 *
 * @param entry the stored entry
 * @returns its business data
 */
function timeRecordingEntryFromJson(entry: StoredTimeRecordingEntry): TimeRecordingEntryState {
    const shared = {
        startDateTime: entry.startDateTime ?? '',
        endDateTime: entry.endDateTime ?? '',
        typeOfWork: entry.typeOfWork,
        billableCode: entry.billableCode as TimeRecordingEntryState['billableCode'],
        // The general entry's restore defaults an absent flag to `true`; keep that here.
        isDurationUnchanged: entry.isDurationUnchanged ?? true,
    };
    return entry.name === GENERAL_ENTRY_NAME
        ? {
              ...shared,
              kind: 'general',
              activityCode: entry.activityCode ?? '',
              activityCodes: (entry.activityCodes ?? []).map(codeFromJson),
          }
        : {
              ...shared,
              kind: 'limited',
              meansOfTransport: entry.meansOfTransport as (TimeRecordingEntryState & {
                  kind: 'limited';
              })['meansOfTransport'],
              destination: entry.destination as (TimeRecordingEntryState & { kind: 'limited' })['destination'],
          };
}

/**
 * @param entry the stored entry's business data
 * @returns the shape the section's restore reads, including the class name it discriminates on
 */
function timeRecordingEntryToJson(entry: TimeRecordingEntryState): unknown {
    const shared = {
        startDateTime: entry.startDateTime,
        endDateTime: entry.endDateTime,
        typeOfWork: entry.typeOfWork,
        billableCode: entry.billableCode,
        isDurationUnchanged: entry.isDurationUnchanged,
    };
    return entry.kind === 'general'
        ? {
              ...shared,
              name: GENERAL_ENTRY_NAME,
              activityCode: entry.activityCode,
              activityCodes: entry.activityCodes.map(codeToJson),
          }
        : {
              ...shared,
              name: LIMITED_ENTRY_NAME,
              meansOfTransport: entry.meansOfTransport,
              destination: entry.destination,
          };
}

/**
 * Narrows the time-recording section. The selected billable preset becomes its code: the legacy payload
 * stored the whole option and the restore matched it by translated display name, so the code is both
 * smaller and language-independent — `applyBillablePreset` re-selects it after the model is built.
 *
 * @param section the flattened time-recording payload
 * @returns its business data
 */
export function timeRecordingFromJson(
    section: FlattenedSections['timeRecording'] | undefined,
): SectionBusinessData<ConfirmationTimeRecordingState> | undefined {
    const preset = section?.billablePresetValue as { model?: StoredValue<string> } | undefined;
    return (
        section && {
            billablePresetCode: preset?.model?.value,
            timeRecordingEntries: ((section.timeRecordingEntries ?? []) as StoredTimeRecordingEntry[]).map(
                timeRecordingEntryFromJson,
            ),
            afterHourCallOutFlag: section.afterHourCallOutFlag,
        }
    );
}

/**
 * @param state the stored time-recording business data
 * @returns the payload `ConfirmationTimeRecordingModelFromJson` reads. `billablePresetValue` is
 * deliberately absent: leaving it out skips the restore's display-name match, and the preset is applied
 * from its code afterwards instead
 */
export function timeRecordingToJson(
    state: ConfirmationTimeRecordingState | undefined,
): FlattenedSections['timeRecording'] | undefined {
    return (
        state &&
        ({
            timeRecordingEntries: (state.timeRecordingEntries ?? []).map(timeRecordingEntryToJson),
            afterHourCallOutFlag: state.afterHourCallOutFlag,
        } as unknown as FlattenedSections['timeRecording'])
    );
}

/** The 25 plain FDA fields the restore reads as they are; its three lists are converted explicitly. */
const FDA2579_FIELDS = [
    'locationName',
    'locationStreet',
    'locationCity',
    'locationState',
    'locationZipCode',
    'locationPhoneNumber',
    'assemblerCompanyName',
    'assemblerStreet',
    'assemblerCity',
    'assemblerState',
    'assemblerZipCode',
    'assemblerPhoneNumber',
    'xRaySystemDropdown',
    'masterControlRoomNumber',
    'dateOfAssembly',
    'masterControl',
    'controlManufacturer',
    'controlModelNumber',
    'controlSerialNumber',
    'manufacturedDate',
    'systemModelName',
    'signatureDate',
    'serviceEngineerEmail',
    'comments',
    'isFda2579FormValid',
] as const satisfies ReadonlyArray<keyof Fda2579FormState>;

/** One stored checkbox: a display decorator whose `model` carries the name and the tick. */
type StoredCheckbox = { model?: { name?: string; value?: unknown } };

/**
 * @param checkbox the stored checkbox
 * @returns which box it is and whether it is ticked
 */
function fda2579CheckboxFromJson(checkbox: StoredCheckbox): Fda2579CheckboxState {
    return { name: checkbox.model?.name ?? '', checked: checkbox.model?.value === true };
}

/** The shape a checkbox is widened back to: the form's restore matches `model.name` and copies `model.value`. */
type CheckboxPayload = { model: { name: string; value: boolean } };

/**
 * @param checkbox the stored checkbox's business data
 * @returns the shape the form's restore matches on — it compares `model.name` and copies `model.value`
 */
function fda2579CheckboxToJson(checkbox: Fda2579CheckboxState): CheckboxPayload {
    return { model: { name: checkbox.name, value: checkbox.checked } };
}

/**
 * Narrows the FDA 2579 form to the technician's entries: the twenty-six plain fields the restore reads
 * as they are, the two checkbox lists reduced to a name and a tick each, and the component rows.
 *
 * The accordion state is dropped as view state, matching the confirmation itself.
 *
 * @param stored the stored FDA form, absent when no form was attached
 * @returns its business data
 */
export function fda2579FormFromJson(stored: JsonFda2579FormModel | undefined): Fda2579FormState | undefined {
    return (
        stored && {
            ...(pick(stored, FDA2579_FIELDS) as Omit<
                Fda2579FormState,
                | 'certifiedComponentCheckboxList'
                | 'intendedUsesCheckboxList'
                | 'otherCertifiedComponentDropdownList'
                | 'componentList'
            >),
            // Every list is defaulted: an older stored form may not have one, and the state's consumers —
            // `fda2579FormToJson` included — should not have to guard for that.
            certifiedComponentCheckboxList: (stored.certifiedComponentCheckboxList ?? []).map(fda2579CheckboxFromJson),
            intendedUsesCheckboxList: (stored.intendedUsesCheckboxList ?? []).map(fda2579CheckboxFromJson),
            otherCertifiedComponentDropdownList: stored.otherCertifiedComponentDropdownList ?? [],
            componentList: storedElements(stored.componentList) as Fda2579ComponentState[],
        }
    );
}

/**
 * Wraps the component rows back into the array shape the form's restore reads. Unlike {@link listToJson},
 * this cannot just wrap `value`: the restore builds `componentList` through `ArrayModelFromJson`
 * (`fda2579-form.model.ts`), whose constructor reads `data.name` and `data.options` unconditionally, so
 * both need to be present or it throws before it ever gets to the elements.
 *
 * The name and cap are fixed rather than stored: the model always builds `componentList` as an uncapped
 * `ArrayModel(FDA2579_COMPONENT_LIST_NAME, [])` (`fda2579-form.model.ts`), so there is nothing about them
 * to round-trip. An empty list is reported as absent instead of an empty array, so the restore's
 * `if (data.componentList)` guard falls through and keeps the constructor's own 15 valueHelper-built rows
 * rather than replacing them with nothing.
 *
 * @param elements the stored component rows
 * @returns the shape the restore reads, or `undefined` when there is nothing to restore
 */
function componentListToJson(elements: readonly Fda2579ComponentState[]): JsonArrayModel | undefined {
    return elements.length === 0
        ? undefined
        : {
              name: ConfigConstants.FDA2579_COMPONENT_LIST_NAME,
              value: [...elements],
              options: { maxEntries: Infinity },
          };
}

/**
 * The payload `fda2579FormToJson` produces — what `Fda2579FormModelFromJson` actually reads, narrower than
 * the legacy `JsonFda2579FormModel` in one respect: `accordionOpenedStatus` is absent, since the restore
 * defaults every flag with `?? false` rather than reading it from `data`.
 */
type Fda2579FormPayload = Omit<
    Fda2579FormState,
    'certifiedComponentCheckboxList' | 'intendedUsesCheckboxList' | 'componentList'
> & {
    certifiedComponentCheckboxList: CheckboxPayload[];
    intendedUsesCheckboxList: CheckboxPayload[];
    componentList: JsonArrayModel | undefined;
};

/**
 * @param state the stored FDA form's business data
 * @returns the payload `Fda2579FormModelFromJson` reads — the checkbox lists in the decorator shape it
 * matches on, the component rows back inside their array wrapper, and everything else as it is
 */
export function fda2579FormToJson(state: Fda2579FormState | undefined): Fda2579FormPayload | undefined {
    return (
        state && {
            ...state,
            certifiedComponentCheckboxList: (state.certifiedComponentCheckboxList ?? []).map(fda2579CheckboxToJson),
            intendedUsesCheckboxList: (state.intendedUsesCheckboxList ?? []).map(fda2579CheckboxToJson),
            otherCertifiedComponentDropdownList: (state.otherCertifiedComponentDropdownList ?? []).map(
                (component: Fda2579OtherComponentState) => ({ ...component }),
            ),
            componentList: componentListToJson(state.componentList ?? []),
        }
    );
}

/**
 * @param list the stored punch list — a `JsonArrayModel` whose entries are each a `TextModel`'s JSON. Real
 * payloads carry `options: {}` with no `maxEntries` (see the spec fixtures), despite `JsonArrayModelOptions`
 * declaring it required, so it is still read defensively.
 * @returns the entry texts and the list's entry cap
 */
function punchListFromJson(list: JsonArrayModel | undefined): PunchListState {
    return {
        entries: (list?.value ?? []).map((entry) => (entry as JsonTextModel | undefined)?.value ?? ''),
        maxEntries: list?.options?.maxEntries,
    };
}

/**
 * Rebuilds the punch list the section's restore assigns onto itself. The entries are deliberately left
 * empty: the restore replaces the list wholesale, and the entries are re-added afterwards through the
 * section's own `addPunchlistItem`, which is what gives each one its validation. The name comes from the
 * paperwork code the constructor uses, so it does not have to be stored.
 *
 * @param list the stored punch list's business data
 * @param code the paperwork code that names this list
 * @returns the shape the restore reads
 */
function punchListToJson(list: PunchListState | undefined, code: string): JsonArrayModel {
    return { name: code, value: [], options: { maxEntries: list?.maxEntries ?? Infinity } };
}

/**
 * Narrows the installation-paperwork section. Almost all of it is already the technician's answers, so the
 * only thing dropped is the notification's question catalogue: the section takes that from
 * `notification.installationPaperworkQuestions` when it is built and the restore never reads it back.
 *
 * The two punch lists and the nested FDA form pass through as stored — see the state type for why.
 *
 * @param section the flattened installation-paperwork payload
 * @returns its business data
 */
export function installationPaperworkFromJson(
    section: FlattenedSections['installationPaperwork'] | undefined,
): SectionBusinessData<ConfirmationInstallationPaperworkState> | undefined {
    if (!section) {
        return undefined;
    }
    const withoutCatalogue = omit(section, 'installationPaperworkQuestions') as Omit<
        SectionBusinessData<ConfirmationInstallationPaperworkState>,
        'siemensPunchList' | 'customerPunchList' | 'attachFDA2579Form'
    >;
    return {
        ...withoutCatalogue,
        magneticCurrent: section.magneticCurrent ? parseFloat(section.magneticCurrent) : undefined,
        siemensPunchList: punchListFromJson(section.siemensPunchList),
        customerPunchList: punchListFromJson(section.customerPunchList),
        attachFDA2579Form: fda2579FormFromJson(section.attachFDA2579Form),
    };
}

/**
 * The payload `installationPaperworkToJson` produces — what
 * `ConfirmationInstallationPaperworkModelFromJson` actually reads, narrower than the legacy
 * `JsonConfirmationInstallationPaperworkModel` in one respect and looser in another:
 *
 * - `installationPaperworkQuestions` is absent: the constructor takes it from the notification, not from
 *   `data` (`confirmation-installation-paperwork.model.ts:121`).
 * - `attachFDA2579Form` stays optional, matching the state it comes from. The restore does not guard for
 *   that — `new Fda2579FormModelFromJson(data.attachFDA2579Form, notification)` runs unconditionally, and
 *   its constructor dereferences `data.isFda2579FormValid` on its first line. The one current producer of
 *   this state (`installationPaperworkFromJson` below) always populates it, so the gap isn't reachable
 *   today — but nothing stops a future producer from omitting it and hitting that throw.
 */
type InstallationPaperworkPayload = Omit<
    FlattenedSections['installationPaperwork'],
    'installationPaperworkQuestions' | 'siemensPunchList' | 'customerPunchList' | 'attachFDA2579Form'
> & {
    siemensPunchList: JsonArrayModel;
    customerPunchList: JsonArrayModel;
    attachFDA2579Form: Fda2579FormPayload | undefined;
};

/**
 * @param state the stored installation-paperwork business data
 * @returns the payload `ConfirmationInstallationPaperworkModelFromJson` reads. The question catalogue stays
 * absent: the restore does not read it, and the section rebuilds it from the notification
 */
export function installationPaperworkToJson(
    state: ConfirmationInstallationPaperworkState | undefined,
): InstallationPaperworkPayload | undefined {
    return (
        state && {
            ...state,
            magneticCurrent: state.magneticCurrent?.toFixed(2),
            siemensPunchList: punchListToJson(
                state.siemensPunchList,
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.siemensPunchList,
            ),
            customerPunchList: punchListToJson(
                state.customerPunchList,
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.customerPunchList,
            ),
            attachFDA2579Form: fda2579FormToJson(state.attachFDA2579Form),
        }
    );
}

/**
 * The accordion flags as the model keeps them: one object, thirteen booleans. `complaintsAA` is declared on
 * the legacy interface but nothing ever writes or reads it — neither `toJSON()` nor the restore names it —
 * so this drops it rather than carrying a field that is always absent.
 *
 * The slice does not store the object; each flag lives on its own section (see {@link AccordionOpenedState}).
 * This shape exists only at the two ends of the conversion, where the model still wants them together.
 */
export type AccordionOpenedStatusPayload = Omit<JsonConfirmationModel['accordionOpenedStatus'], 'complaintsAA'>;

/**
 * The sections a flag belongs to. Each name is both a key of the model's flag object and the field the
 * section is stored under, which is what lets the two functions below map between them by name.
 */
const ACCORDION_SECTIONS = [
    'equipment',
    'complaints',
    'materials',
    'measurementTool',
    'measuringPoints',
    'serviceReport',
    'timeRecording',
    'expenses',
    'uploadToDocBox',
    'participants',
    'sourceReplacements',
    'followup',
    'installationPaperwork',
] as const satisfies ReadonlyArray<keyof AccordionOpenedStatusPayload>;

type AccordionSection = (typeof ACCORDION_SECTIONS)[number];

/** The stored sections, seen as nothing but their accordion flags. `ConfirmationState` satisfies this. */
type SectionsWithAccordion = Readonly<Partial<Record<AccordionSection, AccordionOpenedState | undefined>>>;

/**
 * Reads the model's flag object, defaulting each flag to "not opened".
 *
 * Unlike the section converters this never returns `undefined`: a model carrying only some of the flags, or
 * none, reads back as untouched accordions rather than as no flags at all. The caller then hands each flag
 * to its section via {@link withAccordionOpened}.
 *
 * @param status the model's flag object
 * @returns the flags, every one of them present
 */
export function accordionOpenedStatusFromJson(
    status: Partial<AccordionOpenedStatusPayload> | undefined,
): AccordionOpenedStatusPayload {
    return Object.fromEntries(
        ACCORDION_SECTIONS.map((section) => [section, status?.[section] ?? false]),
    ) as AccordionOpenedStatusPayload;
}

/**
 * Puts one flag on the section it belongs to, leaving an absent section absent — a section the confirmation
 * does not have has no accordion to have been opened.
 *
 * @param section the section's business data, absent when the model built no such section
 * @param accordionOpened its flag
 * @returns the stored section
 */
export function withAccordionOpened<T extends object>(
    section: T | undefined,
    accordionOpened: boolean,
): (T & AccordionOpenedState) | undefined {
    return section && { ...section, accordionOpened };
}

/**
 * Collects the flags back into the one object the model restores them from — the inverse of
 * {@link withAccordionOpened} across all thirteen sections.
 *
 * @param sections the stored confirmation, read for its sections' flags
 * @returns the payload `ConfirmationModelFromJson` reads them back from. It applies a flag only for a
 * section it actually builds, so a flag whose section this confirmation no longer has is dropped there
 */
export function accordionOpenedStatusFromSections(sections: SectionsWithAccordion): AccordionOpenedStatusPayload {
    return Object.fromEntries(
        ACCORDION_SECTIONS.map((section) => [section, sections[section]?.accordionOpened ?? false]),
    ) as AccordionOpenedStatusPayload;
}
